import React, { useState } from 'react';
import { Checkbox, Input, Button, Dropdown, Modal } from 'antd';
import { PlusOutlined, EllipsisOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import type { Trip, TripTaskItem } from '../../types/client';
import { tripDetailEn } from '../../content/trips.en';
import { pendingTaskCount } from '../../utils/tripDayCards';
import styles from './TripChecklistDrawer.module.css';

interface TripChecklistDrawerProps {
  trip: Trip;
  onToggleTask: (taskId: string) => void;
  onDeleteTask: (taskId: string) => void;
  onAddTask: (title: string) => void;
}

const TripChecklistDrawer: React.FC<TripChecklistDrawerProps> = ({
  trip,
  onToggleTask,
  onDeleteTask,
  onAddTask,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [draftTitle, setDraftTitle] = useState('');
  const [deleteSheetOpen, setDeleteSheetOpen] = useState(false);
  const pending = pendingTaskCount(trip);
  const tasks = [...(trip.taskItems ?? [])].sort(
    (a, b) => a.sortOrder - b.sortOrder || a.title.localeCompare(b.title)
  );

  const handleAdd = (): void => {
    const title = draftTitle.trim();
    if (!title) return;
    onAddTask(title);
    setDraftTitle('');
  };

  const menuItems: MenuProps['items'] = [
    {
      key: 'deleteItems',
      label: tripDetailEn.deleteTodoItems,
      disabled: tasks.length === 0,
      onClick: () => setDeleteSheetOpen(true),
    },
  ];

  return (
    <div className={styles.drawer} id="trip-checklist">
      <div className={styles.headerRow}>
        <button
          type="button"
          className={styles.headerButton}
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          aria-label={tripDetailEn.checklistTitle}
          title={expanded ? tripDetailEn.checklistCollapse : tripDetailEn.checklistExpand}
        >
          <span className={styles.checklistIcon} aria-hidden>
            <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
              <path d="M9 3h6a1 1 0 0 1 1 1v1h2a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2V4a1 1 0 0 1 1-1Zm1 2v1h4V5h-4Zm-1 6.5 1.8 1.8L14.5 9l1.4 1.4-4.1 4.1L9 13.9l1-1.4Z" />
            </svg>
          </span>
          <span className={styles.title}>{tripDetailEn.checklistTitle}</span>
          {!expanded && pending > 0 ? <span className={styles.badge}>{pending}</span> : null}
          <span className={styles.chevron} aria-hidden>
            {expanded ? '▴' : '▾'}
          </span>
        </button>

        <Dropdown menu={{ items: menuItems }} trigger={['click']} placement="bottomRight">
          <button
            type="button"
            className={styles.moreButton}
            aria-label={tripDetailEn.checklistMore}
            onClick={(e) => e.stopPropagation()}
          >
            <EllipsisOutlined />
          </button>
        </Dropdown>
      </div>

      {expanded ? (
        <div className={styles.body}>
          {tasks.length === 0 ? (
            <p className={styles.empty}>{tripDetailEn.checklistEmpty}</p>
          ) : (
            <ul className={styles.list}>
              {tasks.map((task: TripTaskItem) => (
                <li key={task.id} className={styles.item}>
                  <Checkbox checked={task.isDone} onChange={() => onToggleTask(task.id)} />
                  <div className={styles.itemText}>
                    <span className={task.isDone ? styles.done : undefined}>{task.title}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <div className={styles.addRow}>
            <Input
              placeholder={tripDetailEn.taskTitlePlaceholder}
              value={draftTitle}
              onChange={(e) => setDraftTitle(e.target.value)}
              onPressEnter={handleAdd}
            />
            <Button type="dashed" icon={<PlusOutlined />} onClick={handleAdd}>
              {tripDetailEn.addTask}
            </Button>
          </div>
        </div>
      ) : null}

      <Modal
        open={deleteSheetOpen}
        title={tripDetailEn.deleteTodoItems}
        onCancel={() => setDeleteSheetOpen(false)}
        footer={
          <Button type="primary" onClick={() => setDeleteSheetOpen(false)}>
            {tripDetailEn.done}
          </Button>
        }
        destroyOnClose
      >
        {tasks.length === 0 ? (
          <p className={styles.empty}>{tripDetailEn.noTasksToDelete}</p>
        ) : (
          <ul className={styles.deleteList}>
            {tasks.map((task) => (
              <li key={task.id} className={styles.deleteItem}>
                <span className={task.isDone ? styles.done : undefined}>{task.title}</span>
                <button
                  type="button"
                  className={styles.removeCircle}
                  aria-label={tripDetailEn.removeTask(task.title)}
                  onClick={() => onDeleteTask(task.id)}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}
      </Modal>
    </div>
  );
};

export default TripChecklistDrawer;
